import { useEnvContext } from "context/EnvContext";
import {
  ASSESSMENT_SLUG,
  CARESEEKER_TYPE,
  CORONA_NOT_TESTED,
  CORONA_TESTED_CURED,
  CORONA_TESTED_NEGATIVE,
  CORONA_TESTED_NOT_CURED,
  CORONA_TEST_PENDING,
  CORONA_UNKNOWN,
  INFECTION_STATUS,
  SEARCH_TYPE_CARE,
  SEARCH_TYPE_HOME_CARE,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_MEDICAL_SUPPLIES,
  SEARCH_TYPE_PSYCHIATRIC_CARE,
  SEARCH_TYPE_REHABILITATION,
  SOLUTION_MOBILE_CARE,
  SOLUTION_SHORT_TERM_STATIC_CARE,
  SOLUTION_STATIC_CARE,
} from "core/consts";
import { isRehabSearch, matchesWhitelistScopes } from "core/model/auctions";
import { formatUnixDate } from "core/model/utils/dates";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  Auction,
  CoronaTest,
  GetOntologyType,
  HospitalStay as HospitalStayType,
  InfectionAndGerms as InfectionAndGermsObjectType,
  LimitedCapacity as LimitedCapacityType,
  MentalHealth as MentalHealthType,
  PatientWhitelistDefinition,
  PatientWhitelistScope,
  PhysicalDisabilities as PhysicalDisabilitiesType,
  RehabilitationPotential as RehabilitationPotentialType,
  SearchType,
  Station,
} from "core/types";
import {
  Divider,
  HorizontalLayout,
  VerticalLayout,
} from "ds_legacy/materials/layouts";
import { margin } from "ds_legacy/materials/metrics";
import { Body, Subheading, Title } from "ds_legacy/materials/typography";
import { useAuctionRequestContext } from "dsl/atoms/Contexts";
import {
  useCareproviderHandlerUrls,
  useCareseekerHandlerUrls,
} from "dsl/hooks/useNavigationHandlers";
import { IcdValue, StaticIcdCodes } from "dsl/static/StaticIcdCodes";
import { StaticIndependenceScaleResult } from "dsl/static/StaticIndependenceScaleResult";
import { getPredicamentValue } from "react-forms-state";
import { useLocale, useTranslations } from "translations";
import Translations from "translations/types";
import { PatientInfoSlugContext } from "./index";
import { medicalCardWhitelist } from "./modelDefinition";
import {
  BooleanField,
  Categories,
  Category,
  CategoryContainer,
  CategoryDescription,
  CategoryType,
  EmptiableCategory,
  EmptiableField,
  OntologyField,
  PatientProfileInfoCard,
  StringField,
  StringValue,
  isInfectionsAndGermsPredicamentNegative,
  isWhitelisted,
} from "./shared";
import {
  ComponentMappingItem,
  Filter,
  activableInputCollectionIsNotEmpty,
  exists,
  getCategories,
  isNumberAndNotZero,
} from "./utils";

const infectionAndGermsWhitelist: PatientWhitelistScope[] = [
  {
    country: "DE",
    search_type: SEARCH_TYPE_REHABILITATION,
  },
  {
    country: "DE",
    search_type: SEARCH_TYPE_HOSPITAL,
  },
  {
    country: "DE",
    search_type: SEARCH_TYPE_CARE,
    solutions: [
      SOLUTION_MOBILE_CARE,
      SOLUTION_SHORT_TERM_STATIC_CARE,
      SOLUTION_STATIC_CARE,
    ],
  },
  {
    country: "DE",
    search_type: SEARCH_TYPE_PSYCHIATRIC_CARE,
  },
];

function DiagnosisDescription({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  if (!value) return null;
  return (
    <HorizontalLayout stretched width="100%">
      <Category
        testId="diagnosis_description"
        title={translations.patient.medicalDiagnosis.mainDiagnosis.label}
      >
        <StringValue isMultiline testId="diagnosis_description" value={value} />
      </Category>
    </HorizontalLayout>
  );
}

function SecondaryDiagnosisDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  if (!value) return null;
  return (
    <HorizontalLayout stretched>
      <Category
        testId="secondary_description"
        title={translations.patient.medicalDiagnosis.secondaryDiagnosis}
      >
        <StringValue isMultiline testId="secondary_description" value={value} />
      </Category>
    </HorizontalLayout>
  );
}

function MorbidityStatus({
  value,
}: {
  oldValue: boolean | null | undefined;
  value: boolean | null | undefined;
}) {
  const translations = useTranslations();
  if (!value) return null;

  return (
    <HorizontalLayout stretched>
      <CategoryContainer testId="morbidity_status">
        <Body>{translations.patient.medicalDiagnosis.multiMorbidPatient}</Body>
      </CategoryContainer>
    </HorizontalLayout>
  );
}

function MedicalHistory({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  if (!value) return null;
  return (
    <Category
      testId="medical_history"
      title={translations.patient.medicalHistory}
    >
      <StringField isMultiline testId="medical_history" value={value} />
    </Category>
  );
}

function AdditionalExaminationsNecessary({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  if (!value) return null;
  return (
    <Category
      testId="additional_examinations_necessary"
      title={translations.patient.additionalExaminations}
    >
      <StringField
        isMultiline
        testId="additional_examinations_necessary"
        value={value}
      />
    </Category>
  );
}

function DependencyDiagnosis({
  value,
}: {
  oldValue: Auction | undefined;
  value: Auction | undefined;
}) {
  const { isSenderApp } = useEnvContext();
  const locale = useLocale();
  const translations = useTranslations();
  const { patient } = useCareseekerHandlerUrls();
  const { goToBarthel, goToEarlyRehab, goToSinger } =
    useCareproviderHandlerUrls();

  const { auctionRequestId, careproviderId } = useAuctionRequestContext();

  if (!value?.patient?.diagnosis?.dependency_diagnosis) return null;

  const dependencyDiagnosis = value?.patient?.diagnosis?.dependency_diagnosis;

  if (!value) return null;

  return (
    <Category
      testId="dependency_diagnosis"
      title={translations.patient.autonomy}
    >
      <StaticIndependenceScaleResult
        goTo={
          isSenderApp
            ? patient.goToBarthel({
                auctionId: value.id,
                patientId: value.patient.id,
              })
            : goToBarthel({
                auctionRequestId: +(auctionRequestId || -1),
                careproviderId: +(careproviderId || -1),
              })
        }
        linkLabel={translations.patient.medicalDiagnosis.barthelLink}
        locale={locale}
        testId="barthel-assessment-result"
        title={translations.patient.medicalDiagnosis.barthelIndexPlaceholder}
        translations={translations}
        value={dependencyDiagnosis?.barthel_assessment}
      />
      <StaticIndependenceScaleResult
        goTo={
          isSenderApp
            ? patient.goToSinger({
                auctionId: value.id,
                patientId: value.patient.id,
              })
            : goToSinger({
                auctionRequestId: +(auctionRequestId || -1),
                careproviderId: +(careproviderId || -1),
              })
        }
        linkLabel={
          translations.patient.medicalDiagnosis.singerPatientProfile.singerLink
        }
        locale={locale}
        testId="singer-assessment-result"
        title={translations.patient.medicalDiagnosis.singerIndexPlaceholder}
        translations={translations}
        value={dependencyDiagnosis?.singer_assessment}
      />
      <StaticIndependenceScaleResult
        goTo={
          isSenderApp
            ? patient.goToEarlyRehab({
                auctionId: value.id,
                patientId: value.patient.id,
              })
            : goToEarlyRehab({
                auctionRequestId: +(auctionRequestId || -1),
                careproviderId: +(careproviderId || -1),
              })
        }
        linkLabel={
          translations.patient.medicalDiagnosis.earlyRehabIndex.earlyRehabLink
        }
        locale={locale}
        testId="early-rehab-assessment-result"
        title={
          translations.patient.medicalDiagnosis
            .earlyRehabBarthelIndexPlaceholder
        }
        translations={translations}
        value={dependencyDiagnosis?.early_rehab_assessment}
      />
      <EmptiableField
        hasNoEmptyValue
        testId="requires_walking_aid"
        title={translations.patient.needsHelpWalking}
        value={getPredicamentValue({
          translations,
          value: dependencyDiagnosis?.requires_walking_aid,
        })}
      />
      <EmptiableField
        hasNoEmptyValue
        testId="requires_support_with_feeding"
        title={translations.patient.needsHelpEating}
        value={getPredicamentValue({
          translations,
          value: dependencyDiagnosis?.requires_support_with_feeding,
        })}
      />
      <EmptiableField
        hasNoEmptyValue
        testId="agitated"
        title={translations.patient.agitationOrientation}
        value={getPredicamentValue({
          translations,
          value: dependencyDiagnosis?.agitated,
        })}
      />
      <StringField
        testId="agitated_description"
        value={dependencyDiagnosis?.agitated_description}
      />
      <EmptiableField
        hasNoEmptyValue
        testId="has_help_at_home"
        title={translations.patient.hasHelpAtHome}
        value={getPredicamentValue({
          translations,
          value: dependencyDiagnosis?.has_help_at_home,
        })}
      />
      <EmptiableField
        hasNoEmptyValue
        testId="returning_home_short_term"
        title={translations.patient.medicalDiagnosis.shortTerm}
        value={getPredicamentValue({
          translations,
          value: dependencyDiagnosis?.returning_home_short_term,
        })}
      />
    </Category>
  );
}

function Dysphagia({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="dysphagia"
      testId="dysphagia"
      title={translations.patient.careNeeds.dysphagia.label}
      value={value}
    />
  );
}

function ChronicPains({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="chronic_pains"
      testId="chronic_pains"
      title={translations.patient.medicalDiagnosis.chronicPains}
      value={value}
    />
  );
}

function Allergies({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="allergies"
      testId="allergies"
      title={translations.patient.medicalDiagnosis.allergies}
      value={value}
    />
  );
}

function IcdCodes({
  value,
}: {
  oldValue: IcdValue | null | undefined;
  value: IcdValue | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <CategoryContainer testId="icd_codes">
      <StaticIcdCodes translations={translations} value={value} />
    </CategoryContainer>
  );
}

type InfectionAndGermsType = {
  infection_and_germs?: InfectionAndGermsObjectType;
  infection_and_germs_state?: number;
};

export function InfectionAndGerms({
  value,
}: {
  oldValue: InfectionAndGermsType | null | undefined;
  value: InfectionAndGermsType;
}) {
  const translations = useTranslations();
  const [negativeState, negativeStateMessage] =
    isInfectionsAndGermsPredicamentNegative(value, translations);

  const withNegativePredicament = negativeState;
  return (
    <Category
      testId="infection_and_germs"
      title={translations.patient.medicalDiagnosis.infections}
    >
      {withNegativePredicament ? (
        <Body>{negativeStateMessage}</Body>
      ) : (
        <>
          <BooleanField
            hasValue={value?.infection_and_germs?.requires_isolation}
            label={
              translations.patient.medicalDiagnosis.infectionsRequiresIsolation
            }
          />
          <BooleanField
            hasValue={value?.infection_and_germs?.mrsa}
            label={translations.patient.medicalDiagnosis.infectionsMRSA}
          />
          <BooleanField
            hasValue={value?.infection_and_germs?.clostridien}
            label={translations.patient.medicalDiagnosis.infectionsClostridien}
          />
          <BooleanField
            hasValue={value?.infection_and_germs?.three_mrgn}
            label={translations.patient.medicalDiagnosis.infectionsThreeMRGN}
          />
          <BooleanField
            hasValue={value?.infection_and_germs?.four_mrgn}
            label={translations.patient.medicalDiagnosis.infectionsFourMRGN}
          />
          <BooleanField
            hasValue={value?.infection_and_germs?.vre}
            label={translations.patient.medicalDiagnosis.infectionsVRE}
          />
          <BooleanField
            hasValue={value?.infection_and_germs?.hepatitis_c}
            label={translations.patient.medicalDiagnosis.infectionsHepatitis}
          />
          <EmptiableField
            prefix={translations.patient.medicalDiagnosis.infectionsOther.label}
            testId="infection_and_germs_other"
            value={value?.infection_and_germs?.other}
          />
        </>
      )}
      <EmptiableField
        hasNoEmptyValue={
          value?.infection_and_germs_state !== INFECTION_STATUS.UNKNOWN
        }
        testId="infection_and_germs_remarks"
        value={value?.infection_and_germs?.remarks || ""}
      />
    </Category>
  );
}

export const coronaSwitch = (
  translations: Translations,
  value: CoronaTest,
): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (value.corona_state === CORONA_NOT_TESTED) {
    return translations.coronaTesting.patientNotTested;
  }

  if (value.corona_state === CORONA_UNKNOWN) {
    return translations.coronaTesting.unknown;
  }

  const testResult = value.corona?.test_result;
  if (!testResult) {
    return undefined;
  }

  switch (testResult) {
    case CORONA_TESTED_CURED:
      return `${translations.coronaTesting.results}${translations.general.colon} ${translations.coronaTesting.positiveCured}`;
    case CORONA_TESTED_NOT_CURED:
      return `${translations.coronaTesting.results}${translations.general.colon} ${translations.coronaTesting.positiveUncured}`;
    case CORONA_TESTED_NEGATIVE:
      return translations.coronaTesting.negative;
    case CORONA_TEST_PENDING:
      return `${translations.coronaTesting.results}${translations.general.colon} ${translations.coronaTesting.pending}`;
    default:
      return undefined;
  }
};

function getVaccinatedValue({
  translations,
  value,
}: {
  translations: Translations;
  value: CoronaTest;
}) {
  if (value?.corona?.vaccinated === "") {
    return translations.coronaTesting.hasBeenVaccinated;
  }
  if (typeof value?.corona?.vaccinated === "string") {
    return `${translations.coronaTesting.hasBeenVaccinated}\n ${value.corona.vaccinated}`;
  }
  return translations.coronaTesting.vaccinationUnknown;
}

export function CoronaVirusTesting({
  value,
}: {
  oldValue: CoronaTest;
  value: CoronaTest;
}) {
  const translations = useTranslations();
  const label = coronaSwitch(translations, value);

  return (
    <CategoryContainer testId="corona_testing">
      <HorizontalLayout>
        <Subheading bold>{translations.coronaTesting.auctionLabel}</Subheading>
      </HorizontalLayout>
      <VerticalLayout margin={margin(0, 0, 0.25, 0)}>
        <EmptiableField testId="corona_testing" value={label} />
        <EmptiableField
          testId="corona_testing_description"
          value={value?.corona?.description}
        />
      </VerticalLayout>
      <VerticalLayout margin={margin(0)}>
        <EmptiableField
          testId="corona_testing_vaccination"
          value={getVaccinatedValue({ translations, value })}
        />
      </VerticalLayout>
    </CategoryContainer>
  );
}

function Addictions({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="addictions"
      testId="addictions"
      title={translations.patient.medicalDiagnosis.addictions}
      value={value}
    />
  );
}

function Vigilance({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.vigilance.key}
      type="vigilance"
      value={value}
    />
  );
}

function Consciousness({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.consciousness.key}
      type="consciousness"
      value={value}
    />
  );
}

function ConsciousnessDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="consciousness_description"
      testId="consciousness_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function ContactBehaviour({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.contactBehaviour.key}
      type="contactBehaviour"
      value={value}
    />
  );
}

function ContactBehaviourDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="contact_behavior_description"
      testId="contact_behavior_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function OrientationTime({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.orientation.keyTime}
      type="orientation"
      value={value}
    />
  );
}

function OrientationTimeDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="orientation_time_description"
      testId="orientation_time_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function OrientationPlace({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.orientation.keyLocation}
      type="orientation"
      value={value}
    />
  );
}

function OrientationPlaceDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="orientation_place_description"
      testId="orientation_place_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function OrientationSituation({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.orientation.keySituation}
      type="orientation"
      value={value}
    />
  );
}

function OrientationSituationDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="orientation_situation_description"
      testId="orientation_situation_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function OrientationPerson({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.orientation.keyPerson}
      type="orientation"
      value={value}
    />
  );
}

function OrientationPersonDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="orientation_person_description"
      testId="orientation_person_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function SelfHarm({
  value,
}: {
  oldValue: MentalHealthType["self_harm"];
  value: MentalHealthType["self_harm"];
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.selfEndangerment.keySelfEndangerment}
      type="selfEndangerment"
      value={value?.state}
    />
  );
}

function SelfHarmDescription({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="self_harm_description"
      testId="self_harm_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function Suicidality({
  value,
}: {
  oldValue: MentalHealthType["suicidality"];
  value: MentalHealthType["suicidality"];
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.selfEndangerment.keySuicidalTendencies}
      type="selfEndangerment"
      value={value?.state}
    />
  );
}

function SuicidalityDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="suicidality_description"
      testId="suicidality_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function Endangerment({
  value,
}: {
  oldValue: MentalHealthType["endangerment_of_others"];
  value: MentalHealthType["endangerment_of_others"];
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.endangermentOfOthers.key}
      type="endangermentOfOthers"
      value={value?.state}
    />
  );
}

function EndangermentDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="endangerment_of_others_description"
      testId="endangerment_of_others_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function SubstanceUse({
  value,
}: {
  oldValue: MentalHealthType["substance_use"];
  value: MentalHealthType["substance_use"];
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.substanceUse.key}
      type="substanceUse"
      value={value?.state}
    />
  );
}

function SubstanceUseDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="substance_use_description"
      testId="substance_use_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function DailyStructure({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.dailyStructure.key}
      type="dailyStructure"
      value={value}
    />
  );
}

function DailyStructureDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="daily_structure_participation_description"
      testId="daily_structure_participation_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function GafScore({ value }: { value: number | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="gaf_score"
      testId="gaf_score"
      title={translations.patient.gafScore.title}
      value={value != null ? String(value) : undefined}
    />
  );
}

function MotivationPatient({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.motivationPatient.key}
      type="motivationPatient"
      value={value}
    />
  );
}

function MotivationPatientDescription({
  value,
}: {
  value: string | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="motivation_description"
      testId="motivation_description"
      title={translations.patient.additionalInformation}
      value={value}
    />
  );
}

function PatientOrientation({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.orientation.key}
      type="orientation"
      value={value}
    />
  );
}

function PatientMotivation({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.motivation.key}
      type="motivation"
      value={value}
    />
  );
}

function PatientCommunication({
  value,
}: {
  oldValue: number | null | undefined;
  value: number | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <OntologyField
      getOntology={getOntology}
      label={translations.ontologies.communication.key}
      type="communication"
      value={value}
    />
  );
}

function Dementia({ auction }: { auction: Auction }) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const isPsychAssessment =
    auction?.search_type === SEARCH_TYPE_PSYCHIATRIC_CARE;
  const mentalHealth = auction?.patient?.diagnosis?.mental_health;

  if (isPsychAssessment) {
    const description = mentalHealth?.dementia_description;
    return (
      <CategoryContainer className="dementia" testId="dementia">
        <OntologyField
          getOntology={getOntology}
          label={translations.ontologies.dementia.key}
          type="dementia"
          value={mentalHealth?.dementia}
        />
        {description != null && description !== "" && (
          <CategoryDescription value={description} />
        )}
      </CategoryContainer>
    );
  }

  return (
    <EmptiableField
      category="dementia"
      testId="dementia"
      title={translations.patient.dementia}
      value={mentalHealth?.dementia_description}
    />
  );
}

function RunAway({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="run_away"
      testId="run_away"
      title={translations.patient.closedAreaNeeded}
      value={value}
    />
  );
}

function ProtectedAreaNeeded({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="protected_area_needed"
      testId="protected_area_needed"
      title={translations.patient.mentalState.protectedAreaNeeded.label}
      value={value}
    />
  );
}

function Nightwatch({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="night_watch"
      testId="night_watch"
      title={translations.patient.mentalState.nightwatch.label}
      value={value}
    />
  );
}

function FacilityCharacteristics({
  value,
}: {
  oldValue: MentalHealthType | null | undefined;
  value: MentalHealthType | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <Category
      testId="factility_characteristics"
      title={translations.patient.mentalState.facilityCharacteristics}
    >
      <BooleanField
        hasValue={value?.closed_station}
        label={translations.patient.mentalState.closedStationRequired}
      />
      <BooleanField
        hasValue={value?.fixation}
        label={translations.patient.mentalState.fixationRequired}
      />
    </Category>
  );
}

function MentalIssues({
  value,
}: {
  oldValue: MentalHealthType | null | undefined;
  value: MentalHealthType | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <Category
      testId="mental_health"
      title={translations.patient.mentalState.mentalIssues}
    >
      <BooleanField
        hasValue={value?.depression}
        label={translations.patient.mentalState.depression}
      />
      <BooleanField
        hasValue={value?.anxiety}
        label={translations.patient.mentalState.anxiety}
      />
      <BooleanField
        hasValue={value?.aggressivity}
        label={translations.patient.mentalState.aggressivity}
      />
      <BooleanField
        hasValue={value?.dangerous}
        label={translations.patient.mentalState.dangerous}
      />
      <StringField
        prefix={translations.patient.mentalState.mentalIssuesOther}
        testId="mental_issues"
        value={value?.mental_issues}
      />
    </Category>
  );
}

function PatientMobility({
  value,
}: {
  oldValue: PhysicalDisabilitiesType | null | undefined;
  value: PhysicalDisabilitiesType | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <Category testId="mobility" title={translations.ontologies.mobility.key}>
      <OntologyField
        getOntology={getOntology}
        type="mobility"
        value={value?.mobility}
      />
      <StringField
        testId="mobility_additional_information"
        value={value?.mobility_additional_information}
      />
    </Category>
  );
}

function VisualImpairment({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="visual_impairment"
      testId="visual_impairment"
      title={translations.patient.mobility.visual_impairment.label}
      value={value}
    />
  );
}

function AuditiveImpairment({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="auditive_impairment"
      testId="auditive_impairment"
      title={translations.patient.mobility.auditive_impairment.label}
      value={value}
    />
  );
}

type AssistiveEquipmentType = Pick<
  PhysicalDisabilitiesType,
  | "assistive_equipment"
  | "existing_medical_supplies"
  | "prescribed_medical_supplies"
  | "required_medical_supplies"
>;

function AssistiveEquipment({
  isNotEmpty,
  translations,
  value,
}: {
  isNotEmpty: boolean;
  oldValue: AssistiveEquipmentType;
  translations: Translations;
  value: AssistiveEquipmentType;
}) {
  const {
    assistive_equipment,
    existing_medical_supplies,
    prescribed_medical_supplies,
    required_medical_supplies,
  } = value;

  const newValue =
    !!existing_medical_supplies ||
    !!prescribed_medical_supplies ||
    !!required_medical_supplies;

  return newValue ? (
    <EmptiableCategory
      isEmpty={isNotEmpty}
      testId="assistive_equipment"
      title={translations.patient.mobility.assistiveEquipment}
    >
      <EmptiableField
        testId="existing_medical_supplies"
        title={translations.patient.medicalSupplies.existingMedicalSupplies}
        value={existing_medical_supplies}
      />
      <EmptiableField
        testId="prescribed_medical_supplies"
        title={translations.patient.medicalSupplies.prescribedMedicalSupplies}
        value={prescribed_medical_supplies}
      />
      <EmptiableField
        testId="required_medical_supplies"
        title={translations.patient.medicalSupplies.neededMedicalSupplies}
        value={required_medical_supplies}
      />
    </EmptiableCategory>
  ) : (
    <EmptiableField
      category="assistive_equipment"
      testId="assistive_equipment"
      title={translations.patient.mobility.assistiveEquipment}
      value={assistive_equipment}
    />
  );
}

function Orthosis({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="orthosis"
      testId="orthosis"
      title={translations.patient.mobility.orthese.label}
      value={value}
    />
  );
}

function Prothesis({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="prothese"
      testId="prothese"
      title={translations.patient.mobility.prothese.label}
      value={value}
    />
  );
}
type HospitalStayProps = {
  hospital_stay?: HospitalStayType;
  search_type?: SearchType;
  station_full?: Station;
};

function HospitalStay({
  isForClinic,
  value,
}: {
  isForClinic: boolean | null | undefined;
  value?: HospitalStayProps | null;
}) {
  const translations = useTranslations();
  const locale = useLocale();

  const isReha = isRehabSearch(value?.search_type);

  const hasCommonFields =
    !!value?.hospital_stay?.doctor_in_charge_in_hospital_phone ||
    !!value?.station_full?.name;

  const hasRehaFields =
    isReha &&
    (!!value?.hospital_stay?.operation_date ||
      !!value?.hospital_stay?.operation_description ||
      !!value?.hospital_stay?.geriatric_early_stage ||
      !!value?.hospital_stay?.doctor_in_charge_in_hospital);

  const notEmpty = hasCommonFields || hasRehaFields;

  const showHospitalStay = isForClinic
    ? isReha || notEmpty
    : isReha && notEmpty;

  if (!showHospitalStay) return null;

  return (
    <EmptiableCategory
      isEmpty={!notEmpty}
      testId="hospital_stay"
      title={translations.patient.medicalDiagnosis.hospitalStay}
    >
      <EmptiableField
        hasNoEmptyValue
        testId="doctor_in_charge_in_hospital"
        title={translations.patient.medicalDiagnosis.doctorInChargeInHospital}
        value={value?.hospital_stay?.doctor_in_charge_in_hospital}
      />
      <EmptiableField
        testId="station_full_name"
        title={translations.patient.station}
        value={value?.station_full?.name}
      />
      <EmptiableField
        hasNoEmptyValue
        testId="doctor_in_charge_in_hospital_phone"
        title={translations.patient.stationPhoneNumber}
        value={value?.hospital_stay?.doctor_in_charge_in_hospital_phone}
      />
      <EmptiableField
        testId="operation_date"
        title={translations.patient.medicalDiagnosis.operationDate}
        value={
          (value?.hospital_stay?.operation_date &&
            formatUnixDate(value?.hospital_stay?.operation_date, locale)) ||
          null
        }
      />
      <EmptiableField
        hasNoEmptyValue
        testId="operation_description"
        title={translations.patient.medicalDiagnosis.operationDescription}
        value={value?.hospital_stay?.operation_description}
      />
      <EmptiableField
        testId="geriatric_early_stage"
        title={translations.patient.medicalDiagnosis.conductedEarlyRehab}
        value={value?.hospital_stay?.geriatric_early_stage}
      />
    </EmptiableCategory>
  );
}

function RehabilitationPotential({
  value,
}: {
  oldValue: RehabilitationPotentialType | null | undefined;
  value: RehabilitationPotentialType | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  return (
    <Category
      testId="rehabilitation_potential"
      title={translations.patient.medicalDiagnosis.rehabilitationPotential}
    >
      <OntologyField
        getOntology={getOntology}
        type="rehabilitationPotential"
        value={value?.evaluation}
      />
      <StringField testId="evaluation_description" value={value?.description} />
    </Category>
  );
}

function RehabilitationGoals({ value }: { value: string | null | undefined }) {
  const translations = useTranslations();
  return (
    <EmptiableField
      category="rehabilitation_goals"
      testId="rehabilitation_goals"
      title={translations.patient.medicalDiagnosis.rehabilitationGoals}
      value={value}
    />
  );
}

function LimitedCapacity({
  isEmpty,
  value,
}: {
  isEmpty: boolean;
  oldValue: LimitedCapacityType | null | undefined;
  value: LimitedCapacityType | null | undefined;
}) {
  const translations = useTranslations();
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="limited_capacity"
      title={translations.patient.medicalDiagnosis.limitations}
    >
      <EmptiableField
        testId="limited_psychological_capacity"
        title={
          translations.patient.medicalDiagnosis.limitedPsychologicalCapacity
        }
        value={value?.limited_psychological_capacity}
      />
      <EmptiableField
        testId="limited_cognitive_capacity"
        title={translations.patient.medicalDiagnosis.limitedCognitiveCapacity}
        value={value?.limited_cognitive_capacity}
      />
      <EmptiableField
        testId="limited_social_behaviour"
        title={translations.patient.medicalDiagnosis.limitedSocialBehaviour}
        value={value?.limited_social_behaviour}
      />
      <EmptiableField
        testId="limited_physical_capacity"
        title={translations.patient.medicalDiagnosis.limitedPsychicalCapacity}
        value={value?.limited_physical_capacity}
      />
      <EmptiableField
        testId="limited_other"
        title={translations.patient.medicalDiagnosis.limitedCapacityOther}
        value={value?.other}
      />
    </EmptiableCategory>
  );
}

function filterCategories(
  categoryKeysToFilter: Array<string>,
  categories: Array<CategoryType>,
) {
  return categories.filter((category) =>
    categoryKeysToFilter.includes(category.key),
  );
}

const medicalDiagnosisFields = [
  "main_diagnosis",
  "secondary_diagnosis",
  "medical_history",
  "additional_examinations_necessary",
  "multimorbidity",
  "icd_codes",
  "dependencyDiagnosis",
  "dysphagia",
  "chronic_pains",
  "allergies",
  "infection_and_germs",
  "addictions",
  "hospital_stay",
  "corona_testing",
  "rehabilitation_potential",
  "limited_capacity",
  "rehabilitation_goals",
];

/** Mental health rows shown for care / rehab / etc. (not psychiatric-care-only). */
const baseMentalStateFields = [
  "vigilance",
  "patient_orientation",
  "patient_motivation",
  "patient_communication",
  "dementia",
  "run_away",
  "protected_area_needed",
  "night_watch",
  "facility_characteristics",
  "mental_issues",
];

/** Psychiatric care: consciousness + axis orientation (person / place / situation / time). */
const psychExpandedMentalStateFields = [
  "consciousness",
  "consciousness_description",
  "orientation_person",
  "orientation_person_description",
  "orientation_place",
  "orientation_place_description",
  "orientation_situation",
  "orientation_situation_description",
  "orientation_time",
  "orientation_time_description",
];

const psychSpecificMentalStateFields = [
  "self_harm",
  "self_harm_description",
  "suicidality",
  "suicidality_description",
  "endangerment_of_others",
  "endangerment_of_others_description",
  "substance_use",
  "substance_use_description",
];

const briefSociopsychiatricAssessmentFields = [
  "contact_behavior",
  "contact_behavior_description",
  "daily_structure_participation",
  "daily_structure_participation_description",
  "gaf_score",
  "motivation",
  "motivation_description",
];

const physicalStateFields = [
  "patient_mobility",
  "visual_impairment",
  "auditive_impairment",
  "assistive_equipment",
  "orthosis",
  "prothesis",
];

export function dependencyDiagnosisExists(auction: Auction | undefined) {
  if (!auction?.patient?.diagnosis?.dependency_diagnosis) {
    return false;
  }

  const {
    agitated,
    barthel_assessment,
    dependency_level,
    early_rehab_assessment,
    has_help_at_home,
    requires_support_with_feeding,
    requires_walking_aid,
    returning_home_short_term,
    singer_assessment,
  } = auction.patient.diagnosis.dependency_diagnosis;

  const hasValidDependencyLevel = isNumberAndNotZero(dependency_level);

  const hasBarthel = !!(
    barthel_assessment?.date && typeof barthel_assessment.score === "number"
  );

  const hasSinger = !!(
    singer_assessment?.date && typeof singer_assessment.score === "number"
  );

  const hasEarlyRehab = !!(
    early_rehab_assessment?.date &&
    typeof early_rehab_assessment.score === "number"
  );

  const requiresWalkingAid = isNumberAndNotZero(requires_walking_aid);
  const requiresSupportWithFeeding = isNumberAndNotZero(
    requires_support_with_feeding,
  );
  const isAgitated = isNumberAndNotZero(agitated);
  const hasHelpAtHome = isNumberAndNotZero(has_help_at_home);
  const returningHomeShortTerm = isNumberAndNotZero(returning_home_short_term);

  return (
    hasValidDependencyLevel ||
    hasBarthel ||
    hasSinger ||
    hasEarlyRehab ||
    requiresWalkingAid ||
    requiresSupportWithFeeding ||
    isAgitated ||
    hasHelpAtHome ||
    returningHomeShortTerm
  );
}

export const medicalFilters: Record<string, Filter> = {
  addictions: {
    exists: (auction) => auction?.patient?.diagnosis?.addictions != null,
    valueGetter: (auction) => auction?.patient?.diagnosis?.addictions,
  },
  additional_examinations_necessary: {
    exists: (auction) =>
      auction?.patient?.diagnosis?.additional_examinations_necessary != null,
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.additional_examinations_necessary,
  },
  allergies: {
    exists: (auction) => auction?.patient?.diagnosis?.allergies != null,
    valueGetter: (auction) => auction?.patient?.diagnosis?.allergies,
  },
  assistive_equipment: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.physical_disabilities?.assistive_equipment != null ||
        diagnosis.physical_disabilities?.prescribed_medical_supplies != null ||
        diagnosis.physical_disabilities?.existing_medical_supplies != null ||
        diagnosis.physical_disabilities?.required_medical_supplies != null
      );
    },
    notEmpty: (auction) =>
      auction?.patient?.diagnosis?.physical_disabilities
        ?.prescribed_medical_supplies != null ||
      auction?.patient?.diagnosis?.physical_disabilities
        ?.existing_medical_supplies != null ||
      auction?.patient?.diagnosis?.physical_disabilities
        ?.required_medical_supplies != null,
    valueGetter: (auction) => ({
      assistive_equipment:
        auction?.patient?.diagnosis?.physical_disabilities?.assistive_equipment,
      existing_medical_supplies:
        auction?.patient?.diagnosis?.physical_disabilities
          ?.existing_medical_supplies,
      prescribed_medical_supplies:
        auction?.patient?.diagnosis?.physical_disabilities
          ?.prescribed_medical_supplies,
      required_medical_supplies:
        auction?.patient?.diagnosis?.physical_disabilities
          ?.required_medical_supplies,
    }),
  },
  auditive_impairment: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.physical_disabilities?.auditive_impairment != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.physical_disabilities?.auditive_impairment,
  },
  chronic_pains: {
    exists: (auction) => auction?.patient?.diagnosis?.chronic_pains != null,
    valueGetter: (auction) => auction?.patient?.diagnosis?.chronic_pains,
  },
  consciousness: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.consciousness != null &&
        diagnosis.mental_health.consciousness > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.consciousness,
  },
  consciousness_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.consciousness_description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.consciousness_description,
  },
  contact_behavior: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      const bsa = diagnosis.mental_health?.brief_sociopsychiatric_assessment;
      return bsa?.contact_behavior != null && bsa.contact_behavior > 0;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.brief_sociopsychiatric_assessment?.contact_behavior,
  },
  contact_behavior_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.brief_sociopsychiatric_assessment
          ?.contact_behavior_description != null
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.brief_sociopsychiatric_assessment?.contact_behavior_description,
  },
  corona_testing: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        typeof diagnosis?.corona_state === "number" ||
        typeof diagnosis?.corona?.test_result === "number" ||
        !!diagnosis?.corona?.description
      );
    },
    valueGetter: (auction) => ({
      corona: auction?.patient?.diagnosis?.corona,
      corona_state: auction?.patient?.diagnosis?.corona_state,
    }),
  },
  daily_structure_participation: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      const bsa = diagnosis.mental_health?.brief_sociopsychiatric_assessment;
      return (
        bsa?.daily_structure_participation != null &&
        bsa.daily_structure_participation > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.brief_sociopsychiatric_assessment?.daily_structure_participation,
  },
  daily_structure_participation_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.brief_sociopsychiatric_assessment
          ?.daily_structure_participation_description != null
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.brief_sociopsychiatric_assessment
        ?.daily_structure_participation_description,
  },
  dementia: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      const isPsychAssessment =
        auction?.search_type === SEARCH_TYPE_PSYCHIATRIC_CARE;
      if (isPsychAssessment) {
        const hasOntology =
          diagnosis.mental_health?.dementia != null &&
          diagnosis.mental_health.dementia > 0;
        const hasDescription =
          diagnosis.mental_health?.dementia_description != null &&
          diagnosis.mental_health.dementia_description !== "";
        return hasOntology || hasDescription;
      }
      return diagnosis.mental_health?.dementia_description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.dementia,
  },
  dependencyDiagnosis: {
    exists: dependencyDiagnosisExists,
    valueGetter: (auction) => auction,
  },
  dysphagia: {
    exists: (auction) => auction?.patient?.diagnosis?.dysphagia != null,
    valueGetter: (auction) => auction?.patient?.diagnosis?.dysphagia,
  },
  endangerment_of_others: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.endangerment_of_others?.state != null &&
        diagnosis.mental_health.endangerment_of_others.state > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.endangerment_of_others,
  },
  endangerment_of_others_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.endangerment_of_others?.description != null
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.endangerment_of_others
        ?.description,
  },
  facility_characteristics: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis?.mental_health) return false;
      return (
        diagnosis.mental_health.closed_station == true ||
        diagnosis.mental_health.fixation == true
      );
    },
    valueGetter: (auction) => auction?.patient?.diagnosis?.mental_health,
  },
  gaf_score: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      const bsa = diagnosis.mental_health?.brief_sociopsychiatric_assessment;
      return bsa?.gaf_score != null && typeof bsa.gaf_score === "number";
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.brief_sociopsychiatric_assessment?.gaf_score,
  },
  hospital_stay: {
    exists: (auction, isForClinic) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;

      const isReha = isRehabSearch(auction?.search_type);

      const hasCommonFields =
        !!diagnosis.hospital_stay?.doctor_in_charge_in_hospital_phone ||
        !!auction.patient?.station_full?.name;

      const hasRehaFields =
        isReha &&
        (!!diagnosis.hospital_stay?.operation_date ||
          !!diagnosis.hospital_stay?.operation_description ||
          !!diagnosis.hospital_stay?.geriatric_early_stage ||
          !!diagnosis.hospital_stay?.doctor_in_charge_in_hospital);

      const notEmpty = hasCommonFields || hasRehaFields;

      const showHospitalStay = isForClinic
        ? isReha || notEmpty
        : isReha && notEmpty;

      if (!showHospitalStay) return false;

      return true;
    },
    valueGetter: (auction) => ({
      hospital_stay: auction?.patient?.diagnosis?.hospital_stay,
      search_type: auction?.search_type,
      station_full: auction?.patient?.station_full,
    }),
  },
  icd_codes: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;

      return !!(diagnosis.icd_code || !!diagnosis.additional_icd_codes?.length);
    },
    valueGetter: (auction) => ({
      additional_icd_codes: auction?.patient?.diagnosis?.additional_icd_codes,
      icd_code: auction?.patient?.diagnosis?.icd_code,
    }),
  },
  infection_and_germs: {
    exists: (auction) =>
      auction?.patient?.diagnosis?.infection_and_germs_state != null,
    valueGetter: (auction) => ({
      infection_and_germs: {
        clostridien:
          auction?.patient?.diagnosis?.infection_and_germs?.clostridien ||
          false,
        description:
          auction?.patient?.diagnosis?.infection_and_germs?.description,
        four_mrgn:
          auction?.patient?.diagnosis?.infection_and_germs?.four_mrgn || false,
        hepatitis_c:
          auction?.patient?.diagnosis?.infection_and_germs?.hepatitis_c ||
          false,
        mrsa: auction?.patient?.diagnosis?.infection_and_germs?.mrsa || false,
        other: auction?.patient?.diagnosis?.infection_and_germs?.other,
        remarks: auction?.patient?.diagnosis?.infection_and_germs?.remarks,
        requires_isolation:
          auction?.patient?.diagnosis?.infection_and_germs
            ?.requires_isolation || false,
        three_mrgn:
          auction?.patient?.diagnosis?.infection_and_germs?.three_mrgn || false,
        vre: auction?.patient?.diagnosis?.infection_and_germs?.vre || false,
      },
      infection_and_germs_state:
        auction?.patient?.diagnosis?.infection_and_germs_state,
    }),
  },
  limited_capacity: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return !!(
        diagnosis.limited_capacity != null &&
        Object.keys(diagnosis.limited_capacity).length &&
        Object.keys(diagnosis.limited_capacity).filter((prop) => prop !== null)
          .length
      );
    },
    notEmpty: (auction) => {
      const limited_capacity = auction?.patient.diagnosis?.limited_capacity;
      return (
        !!limited_capacity &&
        activableInputCollectionIsNotEmpty(limited_capacity)
      );
    },
    valueGetter: (auction) => auction?.patient?.diagnosis?.limited_capacity,
  },
  main_diagnosis: {
    exists: (auction) => auction?.patient?.diagnosis?.main_diagnosis != null,
    valueGetter: (auction) => auction?.patient?.diagnosis?.main_diagnosis,
  },
  medical_history: {
    exists: (auction) => auction?.patient?.diagnosis?.medical_history != null,
    valueGetter: (auction) => auction?.patient?.diagnosis?.medical_history,
  },
  mental_issues: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return !!(
        diagnosis.mental_health?.mental_issues ||
        diagnosis.mental_health?.depression == true ||
        diagnosis.mental_health?.anxiety == true ||
        diagnosis.mental_health?.aggressivity == true ||
        diagnosis.mental_health?.dangerous == true
      );
    },
    valueGetter: (auction) => auction?.patient?.diagnosis?.mental_health,
  },
  motivation: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      const bsa = diagnosis.mental_health?.brief_sociopsychiatric_assessment;
      return bsa?.motivation != null && bsa.motivation > 0;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.brief_sociopsychiatric_assessment?.motivation,
  },
  motivation_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.brief_sociopsychiatric_assessment
          ?.motivation_description != null
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.brief_sociopsychiatric_assessment?.motivation_description,
  },
  multimorbidity: {
    exists: (auction) => auction?.patient?.diagnosis?.multimorbidity != null,
    valueGetter: (auction) => auction?.patient?.diagnosis?.multimorbidity,
  },
  night_watch: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.night_watch_description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.night_watch_description,
  },
  orientation_person: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.orientation_person != null &&
        diagnosis.mental_health.orientation_person > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.orientation_person,
  },
  orientation_person_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.orientation_person_description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.orientation_person_description,
  },
  orientation_place: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.orientation_place != null &&
        diagnosis.mental_health.orientation_place > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.orientation_place,
  },
  orientation_place_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.orientation_place_description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.orientation_place_description,
  },
  orientation_situation: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.orientation_situation != null &&
        diagnosis.mental_health.orientation_situation > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.orientation_situation,
  },
  orientation_situation_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.orientation_situation_description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health
        ?.orientation_situation_description,
  },
  orientation_time: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.orientation_time != null &&
        diagnosis.mental_health.orientation_time > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.orientation_time,
  },
  orientation_time_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.orientation_time_description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.orientation_time_description,
  },
  orthosis: {
    exists: (auction) =>
      auction?.patient?.diagnosis?.physical_disabilities?.orthese != null,
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.physical_disabilities?.orthese,
  },
  patient_communication: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return !!(
        diagnosis.mental_health?.communication &&
        diagnosis.mental_health.communication != 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.communication,
  },
  patient_mobility: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis?.physical_disabilities) return false;
      return (
        (diagnosis.physical_disabilities.mobility &&
          diagnosis.physical_disabilities.mobility > 0) ||
        diagnosis.physical_disabilities.mobility_additional_information != null
      );
    },
    valueGetter: (auction) => ({
      mobility: auction?.patient?.diagnosis?.physical_disabilities?.mobility,
      mobility_additional_information:
        auction?.patient?.diagnosis?.physical_disabilities
          ?.mobility_additional_information,
    }),
  },
  patient_motivation: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return !!(
        diagnosis.mental_health?.motivation &&
        diagnosis.mental_health.motivation != 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.motivation,
  },
  patient_orientation: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return !!(
        diagnosis.mental_health?.orientation &&
        diagnosis.mental_health.orientation != 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.orientation,
  },
  protected_area_needed: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.protected_area_needed != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.protected_area_needed,
  },
  prothesis: {
    exists: (auction) =>
      auction?.patient?.diagnosis?.physical_disabilities?.prothese != null,
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.physical_disabilities?.prothese,
  },
  rehabilitation_goals: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        typeof diagnosis.rehabilitation_goals === "string" &&
        diagnosis.rehabilitation_goals.trim() !== ""
      );
    },
    valueGetter: (auction) => auction?.patient?.diagnosis?.rehabilitation_goals,
  },
  rehabilitation_potential: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.rehabilitation_potential != null &&
        (diagnosis.rehabilitation_potential.description != null ||
          diagnosis.rehabilitation_potential.evaluation != 0)
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.rehabilitation_potential,
  },
  run_away: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.run_away_description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.run_away_description,
  },
  secondary_diagnosis: {
    exists: (auction) =>
      auction?.patient?.diagnosis?.secondary_diagnosis != null,
    valueGetter: (auction) => auction?.patient?.diagnosis?.secondary_diagnosis,
  },
  self_harm: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.self_harm?.state != null &&
        diagnosis.mental_health.self_harm.state > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.self_harm,
  },
  self_harm_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.self_harm?.description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.self_harm?.description,
  },
  substance_use: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.substance_use?.state != null &&
        diagnosis.mental_health.substance_use.state > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.substance_use,
  },
  substance_use_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.substance_use?.description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.substance_use?.description,
  },
  suicidality: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.suicidality?.state != null &&
        diagnosis.mental_health.suicidality.state > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.suicidality,
  },
  suicidality_description: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.mental_health?.suicidality?.description != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.suicidality?.description,
  },
  vigilance: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return (
        diagnosis.mental_health?.vigilance != null &&
        diagnosis.mental_health.vigilance > 0
      );
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.mental_health?.vigilance,
  },
  visual_impairment: {
    exists: (auction) => {
      const diagnosis = auction?.patient?.diagnosis;
      if (!diagnosis) return false;
      return diagnosis.physical_disabilities?.visual_impairment != null;
    },
    valueGetter: (auction) =>
      auction?.patient?.diagnosis?.physical_disabilities?.visual_impairment,
  },
};

function getWhitelist({ formInputValue }: { formInputValue: Auction }) {
  const isPsychiatricCare =
    formInputValue.search_type === SEARCH_TYPE_PSYCHIATRIC_CARE;

  return {
    addictions: true,
    additional_examinations_necessary: true,
    allergies: true,
    assistive_equipment: true,
    auditive_impairment: true,
    chronic_pains: true,
    consciousness: isPsychiatricCare,
    consciousness_description: isPsychiatricCare,
    contact_behavior: isPsychiatricCare,
    contact_behavior_description: isPsychiatricCare,
    corona_testing: true,
    daily_structure_participation: isPsychiatricCare,
    daily_structure_participation_description: isPsychiatricCare,
    dementia: true,
    dependencyDiagnosis: true,
    dysphagia: true,
    endangerment_of_others: isPsychiatricCare,
    endangerment_of_others_description: isPsychiatricCare,
    facility_characteristics: true,
    gaf_score: isPsychiatricCare,
    hospital_stay: true,
    icd_codes: true,
    infection_and_germs: matchesWhitelistScopes(infectionAndGermsWhitelist)({
      formInputValue,
    }),
    limited_capacity: true,
    main_diagnosis: true,
    medical_history: true,
    mental_issues: true,
    motivation: isPsychiatricCare,
    motivation_description: isPsychiatricCare,
    multimorbidity: true,
    night_watch: true,
    orientation_person: isPsychiatricCare,
    orientation_person_description: isPsychiatricCare,
    orientation_place: isPsychiatricCare,
    orientation_place_description: isPsychiatricCare,
    orientation_situation: isPsychiatricCare,
    orientation_situation_description: isPsychiatricCare,
    orientation_time: isPsychiatricCare,
    orientation_time_description: isPsychiatricCare,
    orthosis: true,
    patient_communication: true,
    patient_mobility: true,
    patient_motivation: true,
    patient_orientation: true,
    protected_area_needed: true,
    prothesis: true,
    rehabilitation_goals: true,
    rehabilitation_potential: true,
    run_away: true,
    secondary_diagnosis: true,
    self_harm: isPsychiatricCare,
    self_harm_description: isPsychiatricCare,
    substance_use: isPsychiatricCare,
    substance_use_description: isPsychiatricCare,
    suicidality: isPsychiatricCare,
    suicidality_description: isPsychiatricCare,
    vigilance: true,
    visual_impairment: true,
  };
}

const medicalComponentMapping: ComponentMappingItem[] = [
  {
    Component: DiagnosisDescription,
    fullWidth: true,
    key: "main_diagnosis",
  },
  {
    Component: SecondaryDiagnosisDescription,
    fullWidth: true,
    key: "secondary_diagnosis",
  },
  {
    Component: MorbidityStatus,
    fullWidth: true,
    key: "multimorbidity",
  },
  {
    Component: AdditionalExaminationsNecessary,
    key: "additional_examinations_necessary",
  },
  {
    Component: MedicalHistory,
    key: "medical_history",
  },
  {
    Component: IcdCodes,
    key: "icd_codes",
  },
  {
    Component: InfectionAndGerms,
    key: "infection_and_germs",
  },
  {
    Component: CoronaVirusTesting,
    key: "corona_testing",
  },
  {
    Component: DependencyDiagnosis,
    key: "dependencyDiagnosis",
  },
  {
    Component: Dysphagia,
    key: "dysphagia",
  },
  {
    Component: ChronicPains,
    key: "chronic_pains",
  },
  {
    Component: Allergies,
    key: "allergies",
  },
  {
    Component: Addictions,
    key: "addictions",
  },
  {
    Component: Consciousness,
    key: "consciousness",
  },
  {
    Component: ConsciousnessDescription,
    key: "consciousness_description",
  },
  {
    Component: ContactBehaviour,
    key: "contact_behavior",
  },
  {
    Component: ContactBehaviourDescription,
    key: "contact_behavior_description",
  },
  {
    Component: OrientationPerson,
    key: "orientation_person",
  },
  {
    Component: OrientationPersonDescription,
    key: "orientation_person_description",
  },
  {
    Component: OrientationPlace,
    key: "orientation_place",
  },
  {
    Component: OrientationPlaceDescription,
    key: "orientation_place_description",
  },
  {
    Component: OrientationSituation,
    key: "orientation_situation",
  },
  {
    Component: OrientationSituationDescription,
    key: "orientation_situation_description",
  },
  {
    Component: OrientationTime,
    key: "orientation_time",
  },
  {
    Component: OrientationTimeDescription,
    key: "orientation_time_description",
  },
  {
    Component: SelfHarm,
    key: "self_harm",
  },
  {
    Component: SelfHarmDescription,
    key: "self_harm_description",
  },
  {
    Component: Suicidality,
    key: "suicidality",
  },
  {
    Component: SuicidalityDescription,
    key: "suicidality_description",
  },
  {
    Component: Endangerment,
    key: "endangerment_of_others",
  },
  {
    Component: EndangermentDescription,
    key: "endangerment_of_others_description",
  },
  {
    Component: SubstanceUse,
    key: "substance_use",
  },
  {
    Component: SubstanceUseDescription,
    key: "substance_use_description",
  },
  {
    Component: DailyStructure,
    key: "daily_structure_participation",
  },
  {
    Component: DailyStructureDescription,
    key: "daily_structure_participation_description",
  },
  {
    Component: GafScore,
    key: "gaf_score",
  },
  {
    Component: MotivationPatient,
    key: "motivation",
  },
  {
    Component: MotivationPatientDescription,
    key: "motivation_description",
  },
  {
    Component: Vigilance,
    key: "vigilance",
  },
  {
    Component: PatientOrientation,
    key: "patient_orientation",
  },
  {
    Component: PatientMotivation,
    key: "patient_motivation",
  },
  {
    Component: PatientCommunication,
    key: "patient_communication",
  },
  {
    Component: Dementia,
    key: "dementia",
  },
  {
    Component: RunAway,
    key: "run_away",
  },
  {
    Component: ProtectedAreaNeeded,
    key: "protected_area_needed",
  },
  {
    Component: Nightwatch,
    key: "night_watch",
  },
  {
    Component: FacilityCharacteristics,
    key: "facility_characteristics",
  },
  {
    Component: MentalIssues,
    key: "mental_issues",
  },
  {
    Component: PatientMobility,
    key: "patient_mobility",
  },
  {
    Component: VisualImpairment,
    key: "visual_impairment",
  },
  {
    Component: AuditiveImpairment,
    key: "auditive_impairment",
  },
  {
    Component: AssistiveEquipment,
    key: "assistive_equipment",
  },
  {
    Component: Orthosis,
    key: "orthosis",
  },
  {
    Component: Prothesis,
    key: "prothesis",
  },
  {
    Component: HospitalStay,
    key: "hospital_stay",
  },
  {
    Component: RehabilitationPotential,
    key: "rehabilitation_potential",
  },
  {
    Component: LimitedCapacity,
    key: "limited_capacity",
  },
  {
    Component: RehabilitationGoals,
    key: "rehabilitation_goals",
  },
];

const showMedicalCard = (auction: Auction) => {
  if (auction.assessment_variant === ASSESSMENT_SLUG.MOBILE_SHORT) return false;
  if (
    (
      [SEARCH_TYPE_HOME_CARE, SEARCH_TYPE_MEDICAL_SUPPLIES] as SearchType[]
    ).includes(auction.search_type)
  )
    return false;
  return true;
};

export function MedicalCard({
  auction,
  isForClinic,
}: {
  auction: Auction;
  isForClinic?: boolean;
}) {
  const getOntology = useGetOntology();

  if (!auction.patient.diagnosis) {
    return null;
  }

  if (
    !isWhitelisted(medicalCardWhitelist, auction) ||
    !showMedicalCard(auction)
  ) {
    return null;
  }

  return isForClinic ? (
    <ClinicMedicalCard auction={auction} getOntology={getOntology} />
  ) : (
    <CareproviderMedicalCards auction={auction} getOntology={getOntology} />
  );
}

function ClinicMedicalCard({
  auction,
  getOntology,
}: {
  auction: Auction;
  getOntology: GetOntologyType;
}) {
  const categories = getCategories({
    auction,
    componentMapping: medicalComponentMapping,
    filters: medicalFilters,
    forClinic: true,
    getWhitelist,
  });

  const translations = useTranslations();
  const isPsychAssessment =
    auction.search_type === SEARCH_TYPE_PSYCHIATRIC_CARE;

  const clinicTopCategories = filterCategories(
    medicalDiagnosisFields,
    categories,
  );
  const mentalStateFields = isPsychAssessment
    ? [
        ...baseMentalStateFields,
        ...psychExpandedMentalStateFields,
        ...psychSpecificMentalStateFields,
      ]
    : baseMentalStateFields;
  const mentalStateCategories = filterCategories(mentalStateFields, categories);
  const bsaCategories = isPsychAssessment
    ? filterCategories(briefSociopsychiatricAssessmentFields, categories)
    : [];
  const physicalStateCategories = filterCategories(
    physicalStateFields,
    categories,
  );

  return (
    <PatientProfileInfoCard
      title={translations.patient.medicalDiagnosis.stepperTitle}
    >
      <PatientInfoSlugContext.Provider
        value={{
          assessmentSlug: ASSESSMENT_SLUG.MEDICAL,
          auctionId: auction.id,
          patientId: auction.patient.id,
        }}
      >
        <Categories
          auction={auction}
          categories={clinicTopCategories}
          getOntology={getOntology}
          isForClinic
          translations={translations}
        />
      </PatientInfoSlugContext.Provider>
      {exists(mentalStateCategories) && (
        <>
          <Divider margin={margin(2, 0, 0, 2)} />
          <Title>{translations.patient.mentalHealth}</Title>
          <PatientInfoSlugContext.Provider
            value={{
              assessmentSlug: ASSESSMENT_SLUG.MENTAL,
              auctionId: auction.id,
              patientId: auction.patient.id,
            }}
          >
            <Categories
              auction={auction}
              categories={mentalStateCategories}
              getOntology={getOntology}
              translations={translations}
            />
          </PatientInfoSlugContext.Provider>
        </>
      )}
      {exists(bsaCategories) && (
        <>
          <Divider margin={margin(2, 0, 0, 2)} />
          <Title>{translations.patient.psychEvaluation.sectionTitle}</Title>
          <PatientInfoSlugContext.Provider
            value={{
              assessmentSlug: ASSESSMENT_SLUG.MENTAL,
              auctionId: auction.id,
              patientId: auction.patient.id,
            }}
          >
            <Categories
              auction={auction}
              categories={bsaCategories}
              getOntology={getOntology}
              translations={translations}
            />
          </PatientInfoSlugContext.Provider>
        </>
      )}
      {exists(physicalStateCategories) && (
        <>
          <Divider margin={margin(2, 0, 0, 2)} />
          <Title>{translations.patient.physicalHealth}</Title>
          <PatientInfoSlugContext.Provider
            value={{
              assessmentSlug: ASSESSMENT_SLUG.MOBILITY,
              auctionId: auction.id,
              patientId: auction.patient.id,
            }}
          >
            <Categories
              auction={auction}
              categories={physicalStateCategories}
              getOntology={getOntology}
              translations={translations}
            />
          </PatientInfoSlugContext.Provider>
        </>
      )}
    </PatientProfileInfoCard>
  );
}

const medicalDiagnosisWhitelist: PatientWhitelistDefinition = [
  {
    country: "DE",
    sender_type: CARESEEKER_TYPE.HOSPITAL,
  },
];

const CareproviderMedicalCards = ({
  auction,
  getOntology,
}: {
  auction: Auction;
  getOntology: GetOntologyType;
}) => {
  const translations = useTranslations();
  const isPsychAssessment =
    auction.search_type === SEARCH_TYPE_PSYCHIATRIC_CARE;

  const categories = getCategories({
    auction,
    componentMapping: medicalComponentMapping,
    filters: medicalFilters,
    forClinic: false,
    getWhitelist,
  });

  const medicalDiagnosisCategories = filterCategories(
    medicalDiagnosisFields,
    categories,
  );

  const mentalStateFields = isPsychAssessment
    ? [
        ...baseMentalStateFields,
        ...psychExpandedMentalStateFields,
        ...psychSpecificMentalStateFields,
      ]
    : baseMentalStateFields;
  const mentalStateCategories = filterCategories(mentalStateFields, categories);
  const bsaCategories = isPsychAssessment
    ? filterCategories(briefSociopsychiatricAssessmentFields, categories)
    : [];
  const physicalStateCategories = filterCategories(
    physicalStateFields,
    categories,
  );

  return (
    <>
      {isWhitelisted(medicalDiagnosisWhitelist, auction) && (
        <PatientProfileInfoCard
          title={translations.patient.medicalDiagnosis.stepperTitle}
        >
          <Categories
            auction={auction}
            categories={medicalDiagnosisCategories}
            getOntology={getOntology}
            translations={translations}
          />
        </PatientProfileInfoCard>
      )}
      {exists(mentalStateCategories) && (
        <PatientProfileInfoCard title={translations.patient.mentalHealth}>
          <Categories
            auction={auction}
            categories={mentalStateCategories}
            getOntology={getOntology}
            translations={translations}
          />
        </PatientProfileInfoCard>
      )}
      {exists(bsaCategories) && (
        <PatientProfileInfoCard
          title={translations.patient.psychEvaluation.sectionTitle}
        >
          <Categories
            auction={auction}
            categories={bsaCategories}
            getOntology={getOntology}
            translations={translations}
          />
        </PatientProfileInfoCard>
      )}
      {exists(physicalStateCategories) && (
        <PatientProfileInfoCard title={translations.patient.physicalHealth}>
          <Categories
            auction={auction}
            categories={physicalStateCategories}
            getOntology={getOntology}
            translations={translations}
          />
        </PatientProfileInfoCard>
      )}
    </>
  );
};
